import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  schemas,
  UserRole,
  type ClientDetails,
  type CreateProjectRequest,
  type DuplicateProjectMaterialOverrides,
  type DuplicateProjectRequest,
  type ProjectStatusValue,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, TextField, SelectField, DateField, FormErrorBanner } from '@/components/form';
import { cn } from '@/lib/cn';
import { ClientCreateDialog } from '@/features/clients/components/ClientCreateDialog';
import { useAuth } from '@/features/auth/useAuth';
import { useUsers } from '@/features/users/hooks';
import { useCreateProject, useDuplicateProject } from '@/features/projects/hooks';
import { errorKeyFor } from '@/features/projects/errorMessages';
import { PROJECT_STATUS_VALUES } from '@/features/projects/statusBadge';
import { ClientSearchField } from './ClientSearchField';
import { CreatedByField } from './CreatedByField';
import { ManualPricingFields } from './ManualPricingFields';
import { CloneMaterialsSection } from './CloneMaterialsSection';

type FormValues = {
  clientId: string;
  title: string;
  agentId: string;
  status: ProjectStatusValue;
  deadline: string;
  notes: string;
  manualMaterialCost: string;
  manualOfferedPrice: string;
};

export type ProjectCreateDialogInitialValues = Partial<FormValues>;

interface ProjectCreateDialogProps {
  open: boolean;
  onClose: () => void;
  navigateToCreated?: boolean;
  initialValues?: ProjectCreateDialogInitialValues;
  title?: string;
  /**
   * Set to a project id to turn this into a COPY of that project: the submit goes
   * to `/projects/:id/duplicate`, so the new project arrives with every room,
   * line item and tender setting of the source. The fields below still apply —
   * they are the copy's own header, pre-filled from the source by the caller.
   */
  duplicateFromId?: string;
}

const DEFAULT_VALUES: FormValues = {
  clientId: '',
  title: '',
  agentId: '',
  status: 'IN_PROGRESS',
  deadline: '',
  notes: '',
  manualMaterialCost: '',
  manualOfferedPrice: '',
};

function formValues(initialValues?: ProjectCreateDialogInitialValues): FormValues {
  return {
    ...DEFAULT_VALUES,
    ...initialValues,
    clientId: initialValues?.clientId ?? '',
    title: initialValues?.title ?? '',
    agentId: initialValues?.agentId ?? '',
    status: initialValues?.status ?? 'IN_PROGRESS',
    deadline: initialValues?.deadline ?? '',
    notes: initialValues?.notes ?? '',
    manualMaterialCost: initialValues?.manualMaterialCost ?? '',
    manualOfferedPrice: initialValues?.manualOfferedPrice ?? '',
  };
}

export function ProjectCreateDialog({
  open,
  onClose,
  navigateToCreated = true,
  initialValues,
  title,
  duplicateFromId,
}: ProjectCreateDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateProject();
  const duplicateMut = useDuplicateProject();
  const isCopy = Boolean(duplicateFromId);
  const pending = isCopy ? duplicateMut.isPending : createMut.isPending;
  const error = isCopy ? duplicateMut.error : createMut.error;
  const { user } = useAuth();
  const wasOpenRef = useRef(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  // Filled by the "Materiale" section while it is mounted; read once at submit.
  const materialOverridesRef = useRef<(() => DuplicateProjectMaterialOverrides | undefined) | null>(
    null,
  );

  const agentsQuery = useUsers({
    page: 1,
    pageSize: 100,
    role: UserRole.SALES_AGENT,
    active: true,
    includeDeleted: false,
  });
  const agentOptions = [
    { value: '', label: '—' },
    ...(agentsQuery.data?.items ?? []).map((u) => ({ value: u.id, label: u.fullName })),
  ];
  const statusOptions = PROJECT_STATUS_VALUES.map((status) => ({
    value: status,
    label: t(`projects.status.${status}`),
  }));

  const form = useForm<FormValues>({
    resolver: zodResolver(schemas.createProjectRequest),
    defaultValues: formValues(initialValues),
  });

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(formValues(initialValues));
      createMut.reset();
      duplicateMut.reset();
    }
    wasOpenRef.current = open;
  }, [createMut, duplicateMut, form, initialValues, open]);

  const closeIfIdle = () => {
    if (!pending && !clientDialogOpen) onClose();
  };

  const onClientCreated = (client: ClientDetails) => {
    form.setValue('clientId', client.id, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = async (values: FormValues) => {
    // A copy never carries a status: the server pins it to IN_PROGRESS, which is
    // why the picker is hidden in copy mode.
    const body: DuplicateProjectRequest = {
      clientId: values.clientId,
      title: values.title,
      agentId: values.agentId || undefined,
      deadline: values.deadline || undefined,
      notes: values.notes || undefined,
      manualMaterialCost: values.manualMaterialCost || undefined,
      manualOfferedPrice: values.manualOfferedPrice || undefined,
    };
    const createBody: CreateProjectRequest = { ...body, status: values.status };
    try {
      const created = duplicateFromId
        ? await duplicateMut.mutateAsync({
            sourceId: duplicateFromId,
            // Collected at submit, not kept in form state: the dialog's schema
            // knows nothing about the material section's pickers.
            body: { ...body, materialOverrides: materialOverridesRef.current?.() },
          })
        : await createMut.mutateAsync(createBody);
      if (navigateToCreated) {
        void navigate({ to: '/projects/$id', params: { id: created.id } });
      } else {
        onClose();
      }
    } catch {
      /* surfaced via the banner below */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeIfIdle();
      }}
    >
      <DialogContent className="w-[min(94vw,640px)] max-w-none p-0">
        <DialogHeader className="mb-0 border-b border-border px-6 py-5">
          <DialogTitle className="text-xl font-semibold">
            {title ?? t('projects.create.title')}
          </DialogTitle>
        </DialogHeader>
        <Form form={form} onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          {isCopy && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t('projects.create.cloneHint')}
            </p>
          )}
          <ClientSearchField
            name="clientId"
            label={t('projects.fields.client')}
            placeholder={t('projects.fields.clientPlaceholder') ?? undefined}
            onCreateNew={() => setClientDialogOpen(true)}
          />
          <TextField name="title" label={t('projects.fields.title')} autoFocus />
          <div className={cn('grid gap-4', !isCopy && 'sm:grid-cols-2')}>
            <SelectField name="agentId" label={t('projects.fields.agent')} options={agentOptions} />
            {/* A copy always starts "In lucru" — the server pins it — so offering
                a status here would be a promise the request cannot keep. */}
            {!isCopy && (
              <SelectField
                name="status"
                label={t('projects.fields.status')}
                options={statusOptions}
              />
            )}
          </div>
          <DateField name="deadline" label={t('projects.fields.deadline')} />
          <CreatedByField fullName={user?.fullName ?? ''} />
          <TextField name="notes" label={t('projects.fields.notes')} />

          <ManualPricingFields />

          {isCopy && duplicateFromId && (
            <CloneMaterialsSection projectId={duplicateFromId} collectRef={materialOverridesRef} />
          )}

          <FormErrorBanner error={error} resolveKey={errorKeyFor} />

          <DialogFooter className="mt-0 border-t border-border px-0 pt-4">
            <Button type="button" variant="ghost" onClick={closeIfIdle} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending
                ? t('common.loading')
                : isCopy
                  ? t('projects.create.cloneSubmit')
                  : t('projects.create.submit')}
            </Button>
          </DialogFooter>
        </Form>
        <ClientCreateDialog
          open={clientDialogOpen}
          onClose={() => setClientDialogOpen(false)}
          onCreated={onClientCreated}
        />
      </DialogContent>
    </Dialog>
  );
}
